import { getFontCss, styleConstants } from '@paywithextend/components'
import { useQuery } from '@tanstack/react-query'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Route, Switch, useLocation } from 'react-router'
import { useHistory } from 'react-router-dom'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { selectCreditCardIdFromRoute } from 'src/app/Account/redux/account.selectors'
import AccountBudgets from 'src/app/AccountBudgets/AccountBudgets'
import AccountHistory from 'src/app/AccountHistory/AccountHistory'
import AccountOverview from 'src/app/AccountOverview/AccountOverview'
import AccountSettings from 'src/app/AccountSettings/AccountSettings'
import AccountTransactions from 'src/app/AccountTransactions/AccountTransactions'
import AccountVirtualCards from 'src/app/AccountVirtualCards/AccountVirtualCards'
import { selectPatchingOrganizationInviteFromState } from 'src/app/OrganizationInvites/redux/organizationInvites.selectors'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import { selectReceiptsAutoMatchEnabledFromState } from 'src/app/UploadReceiptsAutoMatchPopup/redux/uploadReceiptsAutoMatch.selectors'
import ReceiptsAutoMatch from 'src/assets/svgs/Receipt-AutoMatch.svg'
import AddToWalletModal from 'src/component-modal/AddToWalletModal/AddToWalletModal'
import PhysicalCardLifecycleModal from 'src/component-modal/PhysicalCardLifecycleModal/PhysicalCardLifecycleModal'
import AccountHeader from 'src/components/AccountHeader/AccountHeader'
import BackgroundPage from 'src/components/BackgroundPage/BackgroundPage'
import { CreateNewDropdownOption } from 'src/components/CreateNewDropdown/CreateNewDropdown'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotFoundBackgroundPage from 'src/components/NotFoundBackgroundPage/NotFoundBackgroundPage'
import NotFoundBackgroundPageContent from 'src/components/NotFoundBackgroundPageContent/NotFoundBackgroundPageContent'
import Subnav, {
  SubnavDesign,
  SubnavOption,
} from 'src/components/Subnav/Subnav'
import SvgTrash from 'src/components/Svgs/SvgTrash'
import Topnav, {
  TopnavAlternativeActionOptions,
} from 'src/components/Topnav/Topnav'
import UserAvatar from 'src/components/UserAvatar/UserAvatar'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { CookieNames, CookieValuePaths } from 'src/cookies'
import { AvatarSizes, Issuers } from 'src/enums'
import CardDetailHero from 'src/feature/CardDetailHero/CardDetailHero'
import { CONDENSED_HERO_HEIGHT } from 'src/feature/CardDetailHero/cardDetailHeroConstants'
import type { PhysicalCardLifecycleAction } from 'src/feature/PhysicalCardLifecycle/physicalCardLifecycle.types'
import type { PhysicalCardLifecycleErrorKind } from 'src/feature/PhysicalCardLifecycle/physicalCardLifecycleErrors'
import { settingsAnchors } from 'src/feature/UpdatedPhysicalCardSettings/settingsAnchors'
import { useBackgroundDispatch, useCookieState } from 'src/hooks'
import { useExtendLocation } from 'src/hooks/locationHooks'
import { useTranslate } from 'src/localization/Trns'
import { CreditCardStatus } from 'src/models/creditCard.models'
import { UserListModel } from 'src/models/user.models'
import { accountsRouteTree } from 'src/navigation/accountsRouteTree'
import { AppLocation } from 'src/navigation/appLocations'
import { Rdrct } from 'src/navigation/Lnk'
import {
  accountActivityUrl,
  accountBudgetsUrl,
  accountHistoryUrl,
  accountOverviewUrl,
  accountSettingsUrl,
  accountStatementsUrl,
  accountTransactionsUrl,
  accountUrl,
  accountVirtualCardsUrl,
  budgetOverviewUrl,
  closeAccountUrl,
} from 'src/navigation/urls'
import AccountActivityTab from 'src/page-components/Accounts/AccountActivityTab/AccountActivityTab'
import AccountBudgetsTab from 'src/page-components/Accounts/AccountBudgetsTab/AccountBudgetsTab'
import AccountOverviewTab from 'src/page-components/Accounts/AccountOverviewTab/AccountOverviewTab'
import AccountStatementsTab from 'src/page-components/Accounts/AccountStatementsTab/AccountStatementsTab'
import {
  useQueryCreditCard,
  useQueryCreditCardPermissions,
} from 'src/queries/creditCard.queries'
import type { CardManagementLifecycleOperationResponse } from 'src/queries/physicalCardManagement/physicalCardManagement.models'
import { physicalCardManagementQueries } from 'src/queries/physicalCardManagement/physicalCardManagement.queries'
import { createSetIsUploadReceiptsAutoMatchPopupOpen } from 'src/redux/actions/app.actions'
import { patchCreditCardStatus } from 'src/redux/actions/data/creditCards.actions'
import { selectIsSidenavLockedOpenFromState } from 'src/redux/selectors/app.selectors'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectFixedElementPushdown } from 'src/redux/selectors/css.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectLoadingFromRemoteConfigState } from 'src/redux/selectors/data/remoteConfig.selectors'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'
import {
  isLiveDataCardHeaderEnabled,
  isUpdatedBudgetsTableEnabled,
  isUpdatedCardActivityEnabled,
  isUpdatedCardOverviewEnabled,
  isUpdatedCardStatementsEnabled,
  isUpdatedCardSummaryEnabled,
  isUpdatedPhysicalCardSettingsEnabled,
} from 'src/utils/featureFlags'
import { css } from 'styled-components'

import { getPhysicalCardLifecycleTerminalOutcome } from './physicalCardLifecycleTerminalOutcome'
import { useAnchorArrivalHighlight } from './useAnchorArrivalHighlight'
import { usePhysicalCardLifecycle } from './usePhysicalCardLifecycle'

/**
 * AccountEntityPage — route-level page for a single account's sub-tabs
 * (EX-39923, supersedes #6986). Reproduces the legacy `src/app/Account/Account`
 * shell faithfully.
 *
 * Tab bar: keeps the landed `src/components/Subnav` (design=Header), NOT
 * FamilyLayout. This is deliberate and parity-driven:
 *   - the entity tab set is CONDITIONAL on card features/permissions (physical-
 *     only card → Transactions+Settings only; closed-card variants differ), and
 *   - the Account Settings tab carries a one-time promo tooltip + sparkle that
 *     FamilyLayout / ext-ui Subnav cannot host (the drop that flagged #6986).
 * Both are reproduced verbatim below. Not touching the shared ext-ui Subnav also
 * satisfies the "do not regress the shared Subnav active-bar" QA pin.
 *
 * Gate: registered once for the whole `/accounts/:id` family (see
 * flatArchitectureRoutes.tsx), so this page + its Subnav stay mounted across
 * sub-tabs and the sliding active bar animates — the same mount-stability
 * guarantee FamilyLayout gives Budgets.
 */
const AccountEntityPage: React.FC = () => {
  const dispatch = useBackgroundDispatch()
  const history = useHistory()

  const creditCardId = useSelector(selectCreditCardIdFromRoute)

  const { data: creditCard, isFetched } = useQueryCreditCard({
    creditCardId: creditCardId,
  })
  const { data: creditCardPermissions } = useQueryCreditCardPermissions({
    creditCardId: creditCardId,
    retry: false,
  })

  const creditCardUser =
    creditCard?.user !== undefined
      ? new UserListModel(creditCard?.user)
      : undefined

  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const extendLocation = useExtendLocation()
  // `key` as well as `hash`: "Adjust limit" is reachable from the hero while the
  // user is ALREADY on Controls & Settings, and pushing the identical URL leaves
  // `hash` untouched. React Router mints a fresh `key` for every push, so it is
  // what lets the arrival treatment below re-run on a repeat click.
  const { hash, key: locationKey } = useLocation()

  const translate = useTranslate()

  const loadingRemoteConfig = useSelector(selectLoadingFromRemoteConfigState)
  const flags = useSelector(selectFeatureFlaggerFromState)
  // EX-40600 — each gated surface reads its own flag OR its own server
  // capability, so an enrolled org sees it with the flags off. Keyed per action:
  // the server decides enrollment and servable limits separately, so no surface
  // may read another's verdict or a limit action's.
  //
  // The capability read is async and fails closed, which is safe here only
  // because these gates drop tab descriptors and swap route CONTENT, never route
  // registration: a pending or failed verdict can leave a tab out of the Subnav,
  // but it can never unmount a route the user is already sitting on. Every
  // consumer reads this one query, so it stays a single request per card view.
  //
  // Hoisted above the hero gate (EX-40599) because that gate now consults it
  // too; the query itself keys off the raw `creditCardId`, so nothing below
  // feeds back into it.
  const { data: cardManagementCapabilities, isFetched: capabilitiesSettled } =
    useQuery(physicalCardManagementQueries.capabilities(creditCardId))
  const capabilities = cardManagementCapabilities?.capabilities
  // `cardDetailHeroGating` was folded into `featureFlags` on this branch, so the
  // hero selector now sits alongside every other flag read rather than in its
  // own module.
  //
  // EX-40599 — flag OR `VIEW_PHYSICAL_CARD_MANAGEMENT`, the server's demo
  // enrollment verdict, so an enrolled org gets the hero without a flag rollout.
  // Unlike the tab gates above this one swaps a HEADER rather than a tab
  // descriptor, so a late-arriving capability is a visible swap from the legacy
  // AccountHeader — see the note on `isUpdatedCardSummaryEnabled`.
  const showCardDetailHero = isUpdatedCardSummaryEnabled(flags, capabilities)
  // EX-40628 — the tab is renamed to "Controls & Settings" only where the
  // redesigned surface actually renders. `AccountSettings` picks its content off
  // this same gate, so a user without it still lands on the legacy Account
  // Settings boxes and must still see them named "Account Settings".
  //
  // EX-40599 — flag OR the demo-enrollment capability, like the gates around it.
  // The "must not drift from the content gate" constraint is what makes passing
  // `capabilities` here mandatory rather than optional: `AccountSettings` reads
  // the same function against the same cache entry, so the rename and the
  // content flip together. The flag alone would rename nothing for an enrolled
  // org that is already being shown the bento.
  const showUpdatedPhysicalCardSettings = isUpdatedPhysicalCardSettingsEnabled(
    flags,
    capabilities,
  )
  const lifecycleSurfacesEnabled =
    showCardDetailHero || showUpdatedPhysicalCardSettings
  const lifecycleCreditCardId = lifecycleSurfacesEnabled
    ? creditCardId
    : undefined
  const { data: cardManagementStatus } = useQuery(
    physicalCardManagementQueries.status(lifecycleCreditCardId),
  )
  const showStatementsTab = isUpdatedCardStatementsEnabled(flags, capabilities)
  const showActivityTab = isUpdatedCardActivityEnabled(flags, capabilities)
  // EX-40596 — one selector for both live-backed hero regions, so the hero is
  // fully demo or fully live rather than a mix. Flag-only, deliberately not
  // OR'd with a capability (see isLiveDataCardHeaderEnabled).
  const liveCardData = isLiveDataCardHeaderEnabled(flags)
  // EX-40632/40633 — mostly a CONTENT swap: Overview already exists and stays in
  // the Subnav either way, so a direct URL resolves with the flag on or off.
  //
  // Flag-only, unlike the two above, and deliberately so: `CardManagementAction`
  // has no action meaning "may view the redesigned Overview" — only the limit,
  // MCC, activity and statements actions exist — so there is no verdict to OR
  // in. Give it the same treatment as its siblings once the backend emits one.
  //
  // One exception, and it is the only way this gate touches the tab set:
  // physical-only cards have no Overview tab today, and the redesigned surface
  // is card-level, so they gain one when the flag is on. See the subnav memo.
  const showUpdatedOverview = isUpdatedCardOverviewEnabled(flags, capabilities)
  // EX-40737 — ONE gate for both budget tables (the Overview snapshot and the
  // Budgets tab's full list), read here and passed down so the two cannot drift:
  // they are the same table under two headings, and a user who follows "view all
  // budgets" must not find the same rows described by different columns.
  //
  // Flag OR the demo-enrollment capability, like the settings and summary gates.
  // A content swap on tabs that exist either way, so a direct URL resolves and a
  // late-arriving capability can only change this body, never unmount it.
  const showUpdatedBudgetsTable = isUpdatedBudgetsTableEnabled(
    flags,
    capabilities,
  )
  // TODO replace cardIsPhysicalOnly check with creditCard.features?.virtualCards when that is ready on BE
  // Hoisted out of the subnav memo below because the Overview tab descriptor
  // and the legacy-path redirect both read it.
  const cardIsPhysicalOnly =
    creditCard?.status === CreditCardStatus.NotApplicable

  /**
   * Which sibling tabs this card actually has, derived ONCE and used by both
   * the subnav memo below and the Overview tab's content.
   *
   * Tab content links out to these tabs, so any surface that renders such a
   * link has to agree with the condition that decides whether the tab exists.
   * A single `!cardIsPhysicalOnly` boolean used to stand in for all three, and
   * it matches none of them — which is how a closed card came to show a
   * Budgets link (Budgets needs `isActive`) and a pending card a Transactions
   * link (Transactions needs one of three states). Three conditions, three
   * flags, one definition each.
   *
   * `=== true` on the optional fields, not truthiness: `features.budgets` is
   * `boolean | undefined`, and `undefined` must read as "no", including where
   * this feeds a query's `enabled` (which defaults to `true` on `undefined`).
   */
  const tabAvailability = useMemo(
    () => ({
      budgets:
        !cardIsPhysicalOnly &&
        creditCard?.features?.budgets === true &&
        creditCard?.isActive === true,
      virtualCards:
        (!cardIsPhysicalOnly && creditCard?.isActive === true) ||
        creditCard?.isClosed === true,
      transactions:
        creditCard?.isActive === true ||
        creditCard?.isClosed === true ||
        cardIsPhysicalOnly,
    }),
    [
      cardIsPhysicalOnly,
      creditCard?.features?.budgets,
      creditCard?.isActive,
      creditCard?.isClosed,
    ],
  )
  // Read here (the page is the redux boundary) and pass down, so the hero and its
  // pinned compact bar stay redux-free. Drives the compact bar's fixed `top` and
  // the swap threshold; a runtime banner shifts both.
  const fixedElementPushdown = useSelector(selectFixedElementPushdown)
  // Also read here for the same reason: the pinned compact bar insets from the
  // left by the sidenav's reserved width so it clears the fixed sidenav.
  const sideNavLockedOpen = useSelector(selectIsSidenavLockedOpenFromState)
  const patchingOrganizationInvite = useSelector(
    selectPatchingOrganizationInviteFromState,
  )
  const receiptsAutoMatchEnabled = useSelector(
    selectReceiptsAutoMatchEnabledFromState,
  )
  const locale = useSelector(selectNullSafeLocaleFromState)
  const currentUser = useCurrentUser()

  const { analytics } = useAnalyticsContext()

  // EX-40599 — the hero's two non-lifecycle ••• actions. Both live here because
  // both need something the hero cannot have: a route change, and the identity
  // comparison behind the cardholder scope.
  const [addToWalletOpen, setAddToWalletOpen] = useState(false)

  // One event with an `action` property, per src/analytics/AGENTS.md, rather
  // than an event per menu item.
  const trackHeaderAction = useCallback(
    (action: 'adjust limit' | 'add to wallet'): void => {
      analytics?.track('physical card header action', {
        action,
        entry_point: 'cardHero',
      })
    },
    [analytics],
  )

  const handleAdjustLimit = useCallback((): void => {
    if (creditCardId === undefined) return
    trackHeaderAction('adjust limit')
    // Same destination the Overview roll-up's "Adjust limit →" builds, so both
    // entry points land on the tile with the same arrival treatment.
    history.push(
      `${accountSettingsUrl(creditCardId)}#${settingsAnchors.spendLimit}`,
    )
  }, [creditCardId, history, trackHeaderAction])

  const handleAddToWallet = useCallback((): void => {
    trackHeaderAction('add to wallet')
    setAddToWalletOpen(true)
  }, [trackHeaderAction])

  /**
   * Add to Wallet is cardholder-scoped: it provisions live credentials to a
   * device, so only the person holding the card may reach it (card-management
   * design handoff §6). A manager looking at someone else's card does not see a
   * disabled item — the item is absent.
   *
   * `=== true` on a resolved pair, not `!==`: both ids are undefined while
   * identity loads, and `undefined === undefined` would hand the action to every
   * viewer for exactly as long as the fetch takes.
   */
  const viewerIsCardholder =
    creditCardUser?.id !== undefined && creditCardUser.id === currentUser?.id

  const trackLifecycleAction = useCallback(
    (action: string, entryPoint?: 'cardHero' | 'cardSettings'): void => {
      analytics?.track('physical card lifecycle', {
        action,
        ...(entryPoint
          ? {
              entry_point:
                entryPoint === 'cardHero' ? 'card_hero' : 'card_settings',
            }
          : {}),
      })
    },
    [analytics],
  )

  const handleLifecycleTerminal = useCallback(
    (
      action: PhysicalCardLifecycleAction,
      operation: CardManagementLifecycleOperationResponse,
      modalOpen: boolean,
    ): void => {
      if (modalOpen) return

      const actionLabel = (() => {
        switch (action) {
          case 'replace':
            return translate('Card replacement')
          case 'lostOrStolen':
            return translate('Lost or stolen card replacement')
          case 'reportFraud':
            return translate('Fraud report')
          case 'cancel':
            return translate('Card cancellation')
        }
      })()
      const outcome = getPhysicalCardLifecycleTerminalOutcome(
        actionLabel,
        operation.status,
        translate,
      )
      dispatch(
        createSetIsToastOpen(true, {
          toastMessage: <p>{outcome.message}</p>,
          failureColorScheme: outcome.failureColorScheme,
        }),
      )
    },
    [dispatch, translate],
  )

  const handleFreezeError = useCallback((): void => {
    dispatch(
      createSetIsToastOpen(true, {
        toastMessage: (
          <p>{translate('The card status could not be changed. Try again.')}</p>
        ),
        failureColorScheme: true,
      }),
    )
  }, [dispatch, translate])

  const handleLifecycleError = useCallback(
    (
      _action: PhysicalCardLifecycleAction,
      _errorKind: PhysicalCardLifecycleErrorKind,
      modalOpen: boolean,
    ): void => {
      if (modalOpen) return
      dispatch(
        createSetIsToastOpen(true, {
          toastMessage: (
            <p>
              {translate(
                'The issuer has not confirmed the outcome. Refresh the card before taking another action.',
              )}
            </p>
          ),
          failureColorScheme: true,
        }),
      )
    },
    [dispatch, translate],
  )

  const lifecycle = usePhysicalCardLifecycle({
    creditCardId: lifecycleCreditCardId,
    expectedVersion: cardManagementStatus?.version,
    cardStatus: cardManagementStatus?.status,
    capabilities,
    hasOrgAdminPermissions: currentUser?.hasOrgAdminPermissions === true,
    canDestroyCard: creditCardPermissions?.canDestroy === true,
    onTerminal: handleLifecycleTerminal,
    onFreezeError: handleFreezeError,
    onLifecycleError: handleLifecycleError,
    onTrack: trackLifecycleAction,
  })

  const canUpdateCreditCard = creditCardPermissions?.canUpdateCreditCard
  const canIssueVirtualCards = creditCardPermissions?.canIssueVirtualCards

  const { updateCookieValue: setShowAccountTutorialPromo } = useCookieState(
    CookieNames.Onboarding,
    CookieValuePaths.shouldShowAccountTutorialPromo,
    true,
  )

  const subnavOptions = useMemo<SubnavOption[]>(() => {
    if (!creditCard) return []

    const overview: SubnavOption = {
      label: <div>{translate('Overview')}</div>,
      to: accountOverviewUrl(creditCard.id),
      isActive: extendLocation.location === AppLocation.AccountOverview,
      ariaLabelSource: 'Overview',
      'data-testid': 'overviewTab',
    }
    const budgets: SubnavOption = {
      label: <div>{translate('Budgets')}</div>,
      to: accountBudgetsUrl(creditCard.id),
      isActive: extendLocation.location === AppLocation.AccountBudgets,
      ariaLabelSource: 'Budgets',
      'data-testid': 'budgetsTab',
    }
    const virtualCards: SubnavOption = {
      label: <div>{translate('Virtual Cards')}</div>,
      to: accountVirtualCardsUrl(creditCard.id),
      isActive: extendLocation.location === AppLocation.AccountVirtualCards,
      ariaLabelSource: 'Virtual Cards',
      'data-testid': 'virtualCardsTab',
    }
    const transactions: SubnavOption = {
      label: <div>{translate('Transactions')}</div>,
      to: accountTransactionsUrl(creditCard.id),
      isActive: extendLocation.location === AppLocation.AccountTransactions,
      ariaLabelSource: 'Transactions',
      'data-testid': 'transactionsTab',
    }
    const statements: SubnavOption = {
      label: <div>{translate('Statements')}</div>,
      to: accountStatementsUrl(creditCard.id),
      isActive: extendLocation.location === AppLocation.AccountStatements,
      ariaLabelSource: 'Statements',
      'data-testid': 'statementsTab',
    }
    const activity: SubnavOption = {
      label: <div>{translate('Activity')}</div>,
      to: accountActivityUrl(creditCard.id),
      isActive: extendLocation.location === AppLocation.AccountActivity,
      ariaLabelSource: 'Activity',
      'data-testid': 'activityTab',
    }
    const accountSettingsLabel = showUpdatedPhysicalCardSettings
      ? translate('Controls & Settings')
      : translate('Account Settings')
    const accountSettings: SubnavOption = {
      label: <div>{accountSettingsLabel}</div>,
      to: accountSettingsUrl(creditCard.id),
      isActive: extendLocation.location === AppLocation.AccountSettings,
      // Mirrors the visible label so the accessible name never announces the
      // old tab name once the redesigned surface is on.
      ariaLabelSource: showUpdatedPhysicalCardSettings
        ? 'Controls & Settings'
        : 'Account Settings',
      'data-testid': 'accountSettingsTab',
      setTooltip: canUpdateCreditCard ? setShowAccountTutorialPromo : undefined,
      tooltipTitle: <div>{translate('New!')}</div>,
      tooltipText: translate(
        '${bold} Reference Fields are now called Expense Categories. Manage them for all Accounts in your Organization under Organization Settings',
        {
          bold: (
            <span
              css={css`
                ${getFontCss(
                  12,
                  styleConstants.colors.white,
                  styleConstants.fontFamilies.medium,
                )};
              `}
            >
              <div>{translate('New!')}</div>
            </span>
          ),
        },
      ),
      sparkle: true,
    }

    const tabs: SubnavOption[] = []
    // Legacy hid Overview for physical-only cards because the legacy body had
    // nothing to say about them — it summarised virtual cards and budgets, and
    // redirected them to Account Settings on arrival.
    //
    // The redesigned Overview is card-level (balance, limit, utilization,
    // statements, recent transactions), so it is exactly the surface a
    // physical-only card wants. Shown for them whenever the flag is on.
    //
    // Gated on the flag rather than lifted outright: with the flag OFF the route
    // still renders legacy `AccountOverview`, which redirects physical-only
    // cards away — an ungated tab there would be a link that bounces.
    if (!cardIsPhysicalOnly || showUpdatedOverview) {
      tabs.push(overview)
    }

    // These three read `tabAvailability` rather than restating their
    // conditions, because the Overview tab's content links to all three and is
    // handed the same object. Restating them here is exactly how the tab bar
    // and the links inside it drifted apart.
    if (tabAvailability.budgets) {
      tabs.push(budgets)
    }

    if (tabAvailability.virtualCards) {
      tabs.push(virtualCards)
    }
    if (tabAvailability.transactions) {
      tabs.push(transactions)
    }
    // EX-40600 — gated on the tab's own flag, deliberately NOT on the card-type
    // conditions above: the design shows both tabs for every card the updated
    // card-management surface covers.
    if (showStatementsTab) {
      tabs.push(statements)
    }
    tabs.push(accountSettings)
    if (showActivityTab) {
      tabs.push(activity)
    }
    return tabs
  }, [
    creditCard?.id,
    creditCard?.status,
    creditCard?.physicalCard.status,
    extendLocation.location,
    canUpdateCreditCard,
    setShowAccountTutorialPromo,
    showStatementsTab,
    showActivityTab,
    showUpdatedPhysicalCardSettings,
    cardIsPhysicalOnly,
    showUpdatedOverview,
    // Replaces the `creditCard?.features?.budgets` entry this memo used to
    // carry — the three tab conditions now live behind this one memoized
    // object, which changes identity only when its own inputs do.
    tabAvailability,
  ])

  const negativeActionOptions = useMemo<CreateNewDropdownOption[]>(() => {
    if (!creditCard || !canUpdateCreditCard || !creditCard.isActive) return []
    return [
      {
        label: <div>{translate('Close Account')}</div>,
        to: closeAccountUrl(creditCard.id),
        ariaLabelSource: translate('Close Account'),
        onClick: () => {
          analytics?.track('close account', {
            intent: 'open close account modal',
            interaction: 'button press',
            screen: accountOverviewUrl(':id'),
          })
        },
        image: <SvgTrash />,
        subtitle: (
          <p
            css={css`
              pointer-events: none;
              ${getFontCss(
                12,
                styleConstants.colors.gray_darkest,
                styleConstants.fontFamilies.regular,
              )}
            `}
          >
            <div>
              {translate(
                'Permanently deactivate this account. All Virtual Cards & Budgets under this account will be closed.',
              )}
            </div>
          </p>
        ),
      },
    ]
  }, [
    creditCard?.id,
    creditCard?.isActive,
    canUpdateCreditCard,
    locale,
    analytics,
  ])

  const alternativeActionOptions = useMemo<
    TopnavAlternativeActionOptions[]
  >(() => {
    const opts: TopnavAlternativeActionOptions[] = []
    if (!creditCard?.partner.isVirtualAccount) {
      opts.push({
        title: <div>{translate('Account Options')}</div>,
        negativeActionOptions,
      })
    }
    if (receiptsAutoMatchEnabled) {
      opts.push({
        title: <div>{translate('Auto-Match Receipts')}</div>,
        titleActions: {
          dispatch: () =>
            dispatch(createSetIsUploadReceiptsAutoMatchPopupOpen(true)),
          ariaLabelSource: 'Auto-Match Receipts',
          imageSrc: ReceiptsAutoMatch,
        },
      })
    }
    return opts
  }, [negativeActionOptions, receiptsAutoMatchEnabled, dispatch, creditCard])

  const patchStatusInterval = useRef<NodeJS.Timeout | undefined>()
  useEffect(() => {
    if (creditCard?.issuer === Issuers.Amex) {
      if (creditCard?.isPending) {
        dispatch(patchCreditCardStatus(creditCardId))
      }
      patchStatusInterval.current = setInterval((): void => {
        if (creditCard?.isPending) {
          dispatch(patchCreditCardStatus(creditCardId))
        }
      }, 60000)
      return (): void => {
        if (patchStatusInterval.current !== undefined) {
          clearInterval(patchStatusInterval.current)
        }
      }
    }
  }, [creditCard?.id, creditCard?.status])

  // Resolve `#fragment` deep links between these tabs — today, "Adjust limit"
  // from the card Overview roll-up and from the hero's ••• menu, both pointing
  // at the Spend Limit tile — and play the design's arrival treatment on the
  // target. See the hook for why anything is needed here at all.
  //
  // `ready` is "the anchor could exist by now", NOT merely "the card loaded".
  // The hook looks the target up exactly once per ready/hash/key change, so
  // firing before the surface that owns the id has decided to render loses the
  // deep link silently.
  //
  // That is a real path since the settings gate gained its capability half
  // (EX-40599): on a direct load of `/settings#card-settings-spend-limit` with
  // the flags off, the card resolves FIRST, `showUpdatedPhysicalCardSettings` is
  // still false because the capability is in flight, and AccountSettings renders
  // the legacy boxes — which carry no tile and no id. The capability then lands,
  // the bento mounts, and `hash`/`locationKey`/card-`isFetched` have all been
  // unchanged throughout, so nothing would re-run.
  //
  // Hence the two-armed condition rather than `&& capabilitiesSettled`: when the
  // surface is ALREADY showing (flag on, or a warm capabilities cache) the
  // anchor exists now and there is nothing to wait for, so the deep link is not
  // delayed behind a query it does not need. Only the undecided case waits. When
  // the verdict is finally "not enrolled", `ready` still flips and the effect
  // runs once against a page with no such id, which is a harmless no-op.
  const anchorTargetCanExist =
    showUpdatedPhysicalCardSettings || capabilitiesSettled
  useAnchorArrivalHighlight({
    hash,
    locationKey,
    ready: isFetched && anchorTargetCanExist,
  })

  if (!isFetched || loadingRemoteConfig || !!patchingOrganizationInvite) {
    return <LoadingBackgroundPage />
  }

  if (!creditCard) {
    return <NotFoundBackgroundPage />
  }

  return (
    <BackgroundPage>
      {/*
        The card-detail hero owns the whole top region (breadcrumb → hero → tabs),
        matching the design. The legacy Topnav "Your Account / Account Options"
        band would sit redundantly above it and is not in the design, so it is
        suppressed whenever the hero renders. NOTE: this Topnav is currently the
        only entry point to Account Options (Close Account) and Auto-Match
        Receipts — those move into the hero's actions menu (EX-40352, slice 3).
      */}
      {!showCardDetailHero && (
        <Topnav
          titleElements={{
            title:
              creditCardUser?.id !== currentUser?.id &&
              creditCardUser !== undefined ? (
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                  `}
                >
                  <UserAvatar
                    size={AvatarSizes.Small}
                    user={creditCardUser}
                    css={css`
                      margin-right: 10px;
                    `}
                  />
                  {translate("${firstName}'s Account", {
                    firstName: creditCardUser.firstNameOrEmail,
                  })}
                </div>
              ) : (
                <div>{translate('Your Account')}</div>
              ),
            scrollContent: (
              <span
                css={css`
                  font-size: 14px;
                  line-height: 18px;
                  ${!canIssueVirtualCards &&
                  css`
                    margin-left: 32px;
                  `}
                `}
              >
                {creditCard.truncatedDisplayName}
              </span>
            ),
          }}
          alternativeActionOptions={alternativeActionOptions}
        />
      )}
      {showCardDetailHero ? (
        <CardDetailHero
          // Identity comes from the id, not from the model: the page already
          // fetches the credit card above, and the hero's seam reads the SAME
          // React Query key, so this adds no second identity fetch.
          creditCardId={creditCardId}
          navigationKey={`${calculatedBackgroundLocation.pathname}${calculatedBackgroundLocation.search}${calculatedBackgroundLocation.hash}`}
          lifecycleControls={lifecycle}
          // `=== true` rather than a cast: the permission is undefined while the
          // permissions query is in flight, and the card name must be read-only
          // until we have a real yes — never editable on an unresolved answer.
          canUpdateCard={canUpdateCreditCard === true}
          fixedElementPushdown={fixedElementPushdown}
          sideNavLockedOpen={sideNavLockedOpen}
          cardHeaderLiveDataEnabled={liveCardData}
          actionsMenuOptions={{
            onAdjustLimit: handleAdjustLimit,
            canAddToWallet: viewerIsCardholder,
            onAddToWallet: handleAddToWallet,
          }}
        />
      ) : (
        <AccountHeader creditCard={creditCard} />
      )}
      {/*
        When the card-detail hero shows, its pinned compact bar (height
        CONDENSED_HERO_HEIGHT) fills the gap between the fixed global header and
        the tabs, so the tab bar must stick that much lower. Off (undefined) for
        every other case, leaving the legacy sticky position unchanged.
      */}
      <Subnav
        design={SubnavDesign.Header}
        options={subnavOptions}
        stickyTopOffset={showCardDetailHero ? CONDENSED_HERO_HEIGHT : undefined}
      />
      <Switch location={calculatedBackgroundLocation}>
        {/*
          EX-40632 — the gate picks the CONTENT, never route registration, so a
          direct URL resolves either way (src/page-components/CLAUDE.md).

          The two redirects are PARITY SURFACE, not new behavior: the legacy
          `AccountOverview` sends budget cards to the Budget Overview and
          physical-only cards to Account Settings, and swapping its body
          wholesale would silently drop both — landing a budget card on a
          physical card's summary. Reproduced declaratively here rather than as
          effects inside the tab, so they resolve before it ever renders.

          Only the BUDGET redirect survives here. The physical-only redirect was
          removed with the matching subnav condition above (they move together,
          or the tab becomes reachable with no way to navigate to it): the
          redesigned Overview is card-level, so it is exactly the surface a
          physical-only card wants. Flag OFF still renders legacy
          `AccountOverview`, which keeps its own physical-only redirect.
        */}
        <Route exact path={accountOverviewUrl(':id')}>
          {!showUpdatedOverview ? (
            <AccountOverview />
          ) : creditCard.isBudget ? (
            <Rdrct to={budgetOverviewUrl(creditCard.id)} />
          ) : (
            <AccountOverviewTab
              creditCard={creditCard}
              creditCardPermissions={creditCardPermissions}
              // The roll-up's Virtual cards cell and budgets footer, the two
              // legacy grids below them, and Recent Transactions' "view all"
              // all link to sibling tabs. Each is suppressed rather than
              // emptied when its tab is absent — a link to a tab that is not in
              // the tab bar leaves the user somewhere the subnav cannot show as
              // active.
              //
              // The SAME object the subnav memo above pushes tabs from, passed
              // down rather than re-derived inside the tab: deriving it in both
              // places is how the two drifted apart in the first place.
              tabAvailability={tabAvailability}
              showUpdatedBudgetsTable={showUpdatedBudgetsTable}
            />
          )}
        </Route>
        {/*
          EX-40737 — a CONTENT swap on a tab that exists either way, so the route
          stays registered and a direct URL resolves with the gate on or off.
        */}
        <Route exact path={accountBudgetsUrl(':id')}>
          {showUpdatedBudgetsTable ? (
            <AccountBudgetsTab creditCardId={creditCardId} />
          ) : (
            <AccountBudgets />
          )}
        </Route>
        <Route exact path={accountVirtualCardsUrl(':id')}>
          <AccountVirtualCards />
        </Route>
        <Route exact path={accountTransactionsUrl(':id')}>
          <AccountTransactions />
        </Route>
        <Route exact path={accountHistoryUrl(':id')}>
          <AccountHistory />
        </Route>
        <Route exact path={accountSettingsUrl(':id')}>
          <AccountSettings
            cardManagementStatus={cardManagementStatus?.status}
            lifecycleControls={lifecycle}
          />
        </Route>
        {/*
          EX-40600 — routes register unconditionally and the GATE picks the
          content (src/page-components/CLAUDE.md: "content routing stays
          unconditional so a direct URL still resolves"). A direct URL to a
          gated-off tab renders the same entity-scoped NotFound the unknown-child
          fallback below produces, so nothing leaks, and a future async gate can
          only swap this body — never unmount the route beneath the user.
        */}
        <Route exact path={accountStatementsUrl(':id')}>
          {showStatementsTab ? (
            <AccountStatementsTab
              creditCardId={creditCardId}
              cardLabel={`${creditCard.truncatedDisplayName} •• ${creditCard.last4}`}
            />
          ) : (
            <NotFoundBackgroundPageContent />
          )}
        </Route>
        <Route exact path={accountActivityUrl(':id')}>
          {showActivityTab ? (
            <AccountActivityTab creditCardId={creditCardId} />
          ) : (
            <NotFoundBackgroundPageContent />
          )}
        </Route>
        <Route exact path={accountUrl(':id')}>
          <Rdrct to={accountsRouteTree.entity.redirectTo(creditCard.id)} />
        </Route>
        <Route path={'/'}>
          <NotFoundBackgroundPageContent />
        </Route>
      </Switch>
      <PhysicalCardLifecycleModal
        state={lifecycle.modalState}
        onClose={lifecycle.closeModal}
        onConfirm={() => void lifecycle.confirmAction()}
        canFreezeAfterFraud={
          cardManagementStatus?.status === 'ACTIVE' &&
          lifecycle.availability.freeze
        }
        onFreeze={() => {
          void lifecycle.toggleFreeze(lifecycle.modalState?.entryPoint)
          lifecycle.closeModal()
        }}
        onViewReplacementCard={(replacementCardId) => {
          lifecycle.closeModal()
          history.push(accountOverviewUrl(replacementCardId))
        }}
      />
      {/*
        Neither wallet is passed, and passing one means having a handler that
        works — the prop pairs the two so a badge cannot be shown without one.
        There is no physical-card provisioning backend: the repo's wallet
        plumbing is keyed by `virtualCardId` end to end and
        `CardManagementAction` carries no provisioning verdict, so nothing can
        answer "may this card go on a phone?" yet. The dialog therefore shows
        its unsupported state, which is the honest answer — and this is where
        `apple` / `google` get wired once the contract lands, one per pipeline
        as the server admits it. Reaching it at all still requires the cardholder scope
        above plus whatever makes the hero render (`show_updated_card_summary`
        OR the demo-enrollment capability — see `showCardDetailHero`).
      */}
      <AddToWalletModal
        open={addToWalletOpen}
        onClose={() => setAddToWalletOpen(false)}
      />
    </BackgroundPage>
  )
}

export default AccountEntityPage
